import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { fetchHardwareDrinks, 
		fetchHWDetailsByDevice, 
		fetchHWIngredientsHistory,
		fetchHardwareUsers,
		fetchHardwareRecentDrinks,
		fetchHardwareMostRepeatedDrinks,
		fetchHardwareCleaningHistory ,
		fetchHardwareLastConnectedHistory
 } from '../../actions/hardware_actions.js';

import { Card, CardBody } from './../../components/card/card.jsx';
import Pagination from "./../../components/pagination/Pagination.jsx";
import { useNavigate } from "react-router-dom";

import { ModalComponent } from '../expand/deviceExpandedModal.jsx';
import { useParams } from "react-router-dom";


import 'lity';
import 'lity/dist/lity.min.css';

function HardwareProfile() {
	const { deviceId } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	var themeColor = getComputedStyle(document.body)
		.getPropertyValue("--bs-theme")
		.trim();
	
	function truncate(str, maxChars = 20) {
		if (typeof str !== 'string') return str;
		if (str.length <= maxChars) return str;
		// reserve 1 char for the ellipsis
		return str.slice(0, maxChars - 1) + '…';
	}
		
	// pull cleaning-history from state
	const cleaningHistory      = useSelector(state => state.hardware.cleaning_history);
	const lastConnectedHistory = useSelector(state => state.hardware.hardware_last_connected_history);

	// const loadingCleaning      = useSelector(state => state.hardware.loading_cleaning_history);
	// const cleaningHistoryError = useSelector(state => state.hardware.cleaning_history_error);

	// 2) Dispatch once on mount
	useEffect(() => {
		if (deviceId && isBarsys360) dispatch(fetchHardwareCleaningHistory(deviceId));
	}, [dispatch, deviceId]);

	useEffect(() => {
		if (deviceId) dispatch(fetchHardwareLastConnectedHistory(deviceId));
	}, [dispatch, deviceId]);

	// 3) Compute lastCleanedData exactly in the same shape your UI expects
	const lastCleanedData = useMemo(() => {
	if (!cleaningHistory) return [];

	// figure out how many rows we need
	const maxRows = cleaningHistory.length
		? Math.max(...cleaningHistory.map(e => e.dates.length))
		: 0;

	return Array.from({ length: maxRows }, (_, rowIdx) => {
		const row = {};

		// always build Station_ A–F
		for (let station = 1; station <= 6; station++) {
		const col = `Station_${String.fromCharCode(64 + station)}`; // 1→A, 2→B...
		const entry = cleaningHistory.find(e => Number(e.station) === station);
		const ts = entry?.dates[rowIdx];

		if (ts) {
			const d = new Date(ts);
			row[col] = {
			time: d.toLocaleTimeString('en-US', {
				hour: '2-digit',
				minute: '2-digit',
				hour12: false
			}),
			date: d.toLocaleDateString('en-US')
			};
		} else {
			row[col] = { time: '--', date: '--' };
		}
		}

		return row;
	});
	}, [cleaningHistory]);

	function getDeviceType(deviceId) {
	if (!deviceId) return 'unknown';
	const id = deviceId.toUpperCase();

	// Barsys Shaker Pro
	if (id.startsWith('BARSYS_S')) return 'shakerpro';

	if (id.startsWith('BARSYS3') || id.startsWith('BARSYS_3') || id.startsWith('BASYS_3')) return 'barsys360';
	if (id.startsWith('BARSYS_C') || id.startsWith('BASYS_C')) return 'coaster';
	return 'unknown';
	}


	const deviceType = getDeviceType(deviceId);
	const isBarsys360 = deviceType === 'barsys360';


	// Drinks Made
	const hardwareDrinks = useSelector(state => state.hardware.hardware_drinks_info.drinks);
	const drinksTotal = useSelector(state => state.hardware.hardware_drinks_info.total);
	// const drinksPage = useSelector(state => state.hardware.hardware_drinks_info.page);
	const drinksPerPage = useSelector(state => state.hardware.hardware_drinks_info.perPage);
	const drinksLoading = useSelector(state => state.hardware.loading_hardware_drinks);
	const [currentPage, setCurrentPage] = useState(1);

	//Stations Details
	const stationDetails = useSelector(state => state.hardware.hardware_details_info); // array
	const stationDetailsLoading = useSelector(state => state.hardware.loading_hw_details);
	const stationDetailsError = useSelector(state => state.hardware.hw_details_error);

	// Ingredients History
	const ingHistory = useSelector(state => state.hardware.hw_ing_history); // array
	const ingHistoryLoading = useSelector(state => state.hardware.loading_hw_ing_history);
	const ingHistoryError = useSelector(state => state.hardware.hw_ing_history_error);

	//HW Users
	const hardwareUsers = useSelector(state => state.hardware.hardware_users_info);
	const hardwareUsersLoading = useSelector(state => state.hardware.loading_hardware_users);
	const hardwareUsersError = useSelector(state => state.hardware.hardware_users_error);

	// Recent Drinks:
	const recentDrinks = useSelector(state => state.hardware.recent_drinks);
	const loadingRecentDrinks = useSelector(state => state.hardware.loading_recent_drinks);

	// Most Repeated Drinks
	const mostRepeatedDrinks = useSelector(state => state.hardware.most_repeated_drinks);
	const loadingMostRepeatedDrinks = useSelector(state => state.hardware.loading_most_repeated_drinks);
	const errorMostRepeatedDrinks = useSelector(state => state.hardware.error_most_repeated_drinks);

	//Fetch Most Repeated Drinks on Mount
	useEffect(() => {
		if (deviceId) {
			dispatch(fetchHardwareMostRepeatedDrinks(deviceId));
		}
	}, [dispatch, deviceId]);
	
	// Fetch Recent Drinks on Mount
	useEffect(() => {
		if (deviceId) {
			dispatch(fetchHardwareRecentDrinks(deviceId));
		}
	}, [dispatch, deviceId]);

	// Fetch HW Users on Mount
	useEffect(() => {
		if (deviceId) {
			dispatch(fetchHardwareUsers(deviceId));
		}
	}, [dispatch, deviceId]);

	// Fetch Ingredients History on Mount
	useEffect(() => {
		if (deviceId  && isBarsys360) {
			dispatch(fetchHWIngredientsHistory(deviceId));
		}
	}, [dispatch, deviceId]);

	//Stations Details Data on Mount
	useEffect(() => {
		if (deviceId && isBarsys360) {
			dispatch(fetchHWDetailsByDevice(deviceId));
		}
	}, [dispatch, deviceId]);

	//Fetch Data on Mount
	useEffect(() => {
		if (deviceId) {
			dispatch(fetchHardwareDrinks({
			deviceId,
			page: currentPage,
			perPage: 20,
			}));
		}
	}, [dispatch, deviceId, currentPage]);

	function truncateText(str, max = 80) {
		if (!str) return "";
			return str.length > max ? str.slice(0, max - 3) + "..." : str;
	}

	// Sample data for each tab
	// const lastConnectedData = [
	// 	{ time: '17:35', date: '19/05/2025' },
	// 	{ time: '18:04', date: '19/05/2025' },
	// 	{ time: '09:12', date: '20/05/2025' },
	// 	{ time: '14:27', date: '21/05/2025' },
	// 	{ time: '11:58', date: '22/05/2025' }
	// ];

	const lastConnectedData = useMemo(() => {
		const src = Array.isArray(lastConnectedHistory) ? lastConnectedHistory : [];
		if (src[0]?.time && src[0]?.date) return src; // already shaped for ModalComponent

		return src.map((v) => {
			const iso = typeof v === 'string' ? v : (v?.timestamp || v?.connected_at || v);
			const d = new Date(iso);
			if (Number.isNaN(d.getTime())) return { time: '--', date: '--' };
			const dd = String(d.getUTCDate()).padStart(2,'0');
			const mm = String(d.getUTCMonth()+1).padStart(2,'0');
			const yyyy = d.getUTCFullYear();
			const hh = String(d.getUTCHours()).padStart(2,'0');
			const min = String(d.getUTCMinutes()).padStart(2,'0');
			return { time: `${hh}:${min}`, date: `${dd}/${mm}/${yyyy}` };
		});
	}, [lastConnectedHistory]);


	

	// const lastCleanedData = [
	// 	{
	// 		Station_A: { time: '17:35', date: '19/05/2025' },
	// 		Station_B: { time: '17:35', date: '19/05/2025' },
	// 		Station_C: { time: '17:35', date: '19/05/2025' },
	// 		Station_D: { time: '17:35', date: '19/05/2025' },
	// 		Station_E: { time: '17:35', date: '19/05/2025' },
	// 		Station_F: { time: '17:35', date: '19/05/2025' }
	// 	},
	// 	{
	// 		Station_A: { time: '18:04', date: '19/05/2025' },
	// 		Station_B: { time: '18:04', date: '19/05/2025' },
	// 		Station_C: { time: '18:04', date: '19/05/2025' },
	// 		Station_D: { time: '18:04', date: '19/05/2025' },
	// 		Station_E: { time: '18:04', date: '19/05/2025' },
	// 		Station_F: { time: '18:04', date: '19/05/2025' }
	// 	}
	// ];

	const ingredientsHistoryData = [
		{ ingredient: 'Vodka', count: 10 },
		{ ingredient: 'Rum', count: 8 },
		{ ingredient: 'Coffee Liqueur', count: 6 },
		{ ingredient: 'Tequila', count: 5 },
		{ ingredient: 'Lemon Juice', count: 4 },
		{ ingredient: 'Orange Liqueur', count: 4 },
		{ ingredient: 'Cranberry Juice', count: 3 },
		{ ingredient: 'Whiskey', count: 2 },
		{ ingredient: 'Bourbon', count: 2 },
		{ ingredient: 'Orange Juice', count: 1 },
		{ ingredient: 'Gin', count: 1 }
	];

	function useThemeColor() {
		const [themeColor, setThemeColor] = React.useState(() =>
			getComputedStyle(document.body).getPropertyValue("--bs-theme").trim()
		);

		useEffect(() => {
			const observer = new MutationObserver(() => {
			const newColor = getComputedStyle(document.body).getPropertyValue("--bs-theme").trim();
			setThemeColor(newColor);
			});

			observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

			return () => observer.disconnect();
		}, []);

		return themeColor;
	}


// this will be light on dark-mode, dark on light-mode
const inverseColor = getComputedStyle(document.body)
  .getPropertyValue("--bs-inverse")
  .trim();





	//Functions of UI Render Parts
	function RecentUsers() {
	// Get the users from redux and sort by last_activity desc
	const sortedUsers = (hardwareUsers || [])
		.filter(u => !!u) // filter out any undefined/null
		.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity))
		.slice(0, 3);

		return (
			<div>
				{sortedUsers.map((user, idx) => (
					<div key={user.id || idx} className="d-flex align-items-center mb-3">
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: '50%',
							overflow: 'hidden',
							background: '#d2dae4',
							display: 'inline-block',
							flexShrink: 0,
							flexGrow: 0,
						}}
						>
						<img
							src={user.userDisplayImage || "/assets/img/usershimmer.png"}
							alt={user.username || "User"}
							style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							borderRadius: '50%',
							display: 'block',
							}}
						/>
					</div>

					<div className="flex-fill px-3">
						<div className="fw-bold text-truncate w-100px" style={{ fontSize: 15 }}>
						{user.username}
						</div>
						<div className="fs-12px text-inverse text-opacity-50" style={{ lineHeight: 1.1 }}>
						{user.last_activity
							? new Date(user.last_activity).toLocaleDateString("en-US", {
								month: "2-digit",
								day: "2-digit",
								year: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							})
							: "--"}
						</div>
					</div>
				</div>
				))}
			</div>
		);
	}

	const [activeTab, setActiveTab] = useState('drinks-made');


	return (
		<Card>
			<CardBody className="p-0">
				<div className="profile">
					<div className="profile-container">
						<div className="profile-sidebar">
							<div className="desktop-sticky-top">
								<div className="profile-img">
									<img
										src={
											deviceType === 'barsys360'
												? "/assets/img/barsys360.jpeg"
												: deviceType === 'coaster'
												? "/assets/img/BarsysCoaster.jpg"
												: deviceType === 'shakerpro'
												? "/assets/img/Shaker-Pro.jpg"
												: "/assets/img/glassshimmer.png"
										}
										alt={
										deviceType === 'barsys360'
											? "Barsys 360"
											: deviceType === 'coaster'
											? "Barsys Coaster"
											: deviceType === 'shakerpro'
											? "Barsys Shaker Pro"
											: "Barsys Device"
										}										
										style={{
											borderRadius: '12px',        
											width: "100%",               
											height: "auto",             
											objectFit: "cover" 
  										}}
									/>
								</div>
								
								<h4>
									{deviceType === 'barsys360'
									? 'Barsys 360'
									: deviceType === 'coaster'
									? 'Barsys Coaster'
									: deviceType === 'shakerpro'
									? 'Barsys Shaker Pro'
									: 'Barsys Device'}
								</h4>
								<div style={{ marginBottom: 8 }}>
									{deviceId}
								</div>

								{/* <p>
									Principal UXUI Design & Brand Architecture for HUD. Creator of SeanTheme.
									Bringing the world closer together. Studied Computer Science and Psychology at Harvard University.
								</p> */}
								{/* <div className="mb-1">
									<i className="fa fa-map-marker-alt fa-fw text-inverse text-opacity-50"></i> New York, NY
								</div>
								<div className="mb-3">
									<i className="fa fa-link fa-fw text-inverse text-opacity-50"></i> seantheme.com/hud
								</div> */}
						
								<hr className="mt-4 mb-4" />
						
								<div className="d-flex justify-content-between align-items-center mb-3">
									<div className="fw-bold fs-16px">Users</div>
									<button type="button" 
											className="btn btn-sm btn-outline-theme"
											    onClick={() =>
														// fire the Bootstrap tab toggle for #my-devices:
														document
															.querySelector('a[href="#station-users"][data-bs-toggle="tab"]')
															.click()
														}
									>
										View
									</button>
								</div>
								{RecentUsers()}
								{/* <div className="d-flex align-items-center mb-3">
									<img src="/assets/img/user/user-4.jpg" alt="" width="30" className="rounded-circle" />
									<div className="flex-fill px-3">
										<div className="fw-bold text-truncate w-100px">Kenny Bright</div>
										<div className="fs-12px text-inverse text-opacity-50">1.4m followers</div>
									</div>
									<a href="#/" className="btn btn-sm btn-outline-theme fs-11px">Follow</a>
								</div>
								<div className="d-flex align-items-center">
									<img src="/assets/img/user/user-5.jpg" alt="" width="30" className="rounded-circle" />
									<div className="flex-fill px-3">
										<div className="fw-bold text-truncate w-100px">Cara Poole</div>
										<div className="fs-12px text-inverse text-opacity-50">989k followers</div>
									</div>
									<a href="#/" className="btn btn-sm btn-outline-theme fs-11px">Follow</a>
								</div> */}
							</div>
						</div>
						
						<div className="profile-content">
							<ul className="profile-tab nav nav-tabs nav-tabs-v2">
								<li className="nav-item">
									<a
									href="#drinks-made"
									className={`nav-link ${activeTab === 'drinks-made' ? 'active' : ''}`}
									data-bs-toggle="tab"
									onClick={() => setActiveTab('drinks-made')}
									>
									<div className="nav-field">Drinks Made {drinksTotal ? `(${drinksTotal})` : ""}</div>
									</a>
								</li>
								  {isBarsys360  && (
									<li className="nav-item">
									<a
										href="#station-details"
										className={`nav-link ${activeTab === 'station-details' ? 'active' : ''}`}
										data-bs-toggle="tab"
										onClick={() => setActiveTab('station-details')}
									>
										<div className="nav-field">Station Details</div>
									</a>
									</li>
								)}
								<li className="nav-item">
									<a
									href="#station-users"
									className={`nav-link ${activeTab === 'station-users' ? 'active' : ''}`}
									data-bs-toggle="tab"
									onClick={() => setActiveTab('station-users')}
									>
									<div className="nav-field">
										Users {hardwareUsers && hardwareUsers.length > 0 ? `(${hardwareUsers.length})` : "(0)"}
									</div>

									</a>
								</li>
							</ul>
							<div className="profile-content-container">
								<div className="row gx-4">
									<div className="col-xl-8">
										<div className="tab-content p-0">
											<div className={`tab-pane fade${activeTab === 'drinks-made' ? ' show active' : ''}`} id="drinks-made">
												<Card>
													<CardBody>
													{drinksLoading ? (
														<div style={{ textAlign: 'center', padding: '24px' }}>
														<span className="spinner-border spinner-border-sm" /> Loading drinks...
														</div>
													) : hardwareDrinks.length === 0 ? (
														<div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
														No drinks made by this device.
														</div>
													) : (
														<>
														{hardwareDrinks.map((c, idx) => (
															<div
															key={c.user_event_id || idx}
															style={{
																display: 'flex',
																alignItems: 'center',
																border: '1px solid gray',
																borderRadius: '8px',
																padding: '16px',
																marginBottom: '5px',
															}}
															>
															{/* Row Number */}
															<div style={{
																width: '50px',
																textAlign: 'center',
																fontWeight: 'bold',
																color: themeColor,
																paddingRight: '20px'
															}}>
																{(currentPage - 1) * drinksPerPage + idx + 1}
															</div>
															{/* Image */}
															<img
																src={c.img || "/assets/img/glassshimmer.png"}
																alt={c.drink_name}
																onError={e => { e.target.onerror = null; e.target.src = "/assets/img/glassshimmer.png"; }}
																style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
															/>
															{/* Name */}
															<div style={{ flex: 1, marginLeft: '16px', minWidth: 0 }}>
																<h5 className='text-inverse text-opacity-70'
																	style={{ margin: 0, fontSize: '18px', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
																	{c.drink_name}
																</h5>
																<small style={{ color: inverseColor, fontSize: '0.85rem' }}>
																	Created On: {" "}   
																	{new Date(c.created * 1000)
																		.toLocaleString('en-US', {
																			dateStyle:   'short',   // e.g. "6/11/25"
																			timeStyle:   'short',   // e.g. "4:48 PM"
																		})
																	}
																</small>	
															</div>
	
															{/* Ingredients - perfectly aligned */}
															<div
																style={{
																flex: 1,
																marginLeft: '16px',
																display: 'grid',
																gridTemplateColumns: '68px 1fr',
																rowGap: '1px',
																alignItems: 'center',
																minWidth: 0,
																fontSize: 15
																}}
															>
																<span style={{ color: themeColor, fontWeight: 500, fontSize: 15, whiteSpace: 'nowrap' }}>Base:</span>
																<span
																title={c.base}
																style={{
																	color: inverseColor,
																	textOverflow: 'ellipsis',
																	overflow: 'hidden',
																	whiteSpace: 'nowrap',
																	maxWidth: 220,
																	display: 'block'
																}}
																>{truncateText(c.base)}</span>

																<span style={{ color: themeColor, fontWeight: 500, fontSize: 15, whiteSpace: 'nowrap' }}>Mixer:</span>
																<span
																title={c.mixer}
																style={{
																	color: inverseColor,
																	textOverflow: 'ellipsis',
																	overflow: 'hidden',
																	whiteSpace: 'nowrap',
																	maxWidth: 220,
																	display: 'block'
																}}
																>{truncateText(c.mixer)}</span>

																<span style={{ color: themeColor, fontWeight: 500, fontSize: 15, whiteSpace: 'nowrap' }}>Garnish:</span>
																<span
																title={c.garnish}
																style={{
																	color: inverseColor,
																	textOverflow: 'ellipsis',
																	overflow: 'hidden',
																	whiteSpace: 'nowrap',
																	maxWidth: 220,
																	display: 'block'
																}}
																>{truncateText(c.garnish)}</span>
															</div>
															</div>
														))}
														{/* Pagination */}
														<div style={{ marginTop: 12 }}>
															<Pagination
															currentPage={currentPage}
															itemsPerPage={drinksPerPage}
															totalItems={drinksTotal}
															paginate={setCurrentPage}
															/>
														</div>
														</>
													)}
													</CardBody>
												</Card>
											</div>

											<div className={`tab-pane fade${activeTab === 'station-users' ? ' show active' : ''}`} id="station-users">
												<Card style={{ background: "transparent", boxShadow: "none" }}>
													<CardBody>
														<div>
															{hardwareUsers && hardwareUsers.length > 0 ? (
															hardwareUsers.map((user, idx) => (
																<div
																key={user.id || idx}
																style={{
																	display: 'flex',
																	alignItems: 'center',
																	background: 'transparent',
																	border: '1px solid #6c757d',
																	borderRadius: '16px',
																	padding: '24px 32px',
																	marginBottom: '24px',
																	minHeight: '110px'
																}}
																>
																{/* Number badge */}
																<div
																	style={{
																	minWidth: '36px',
																	height: '36px',
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	fontWeight: 700,
																	color: inverseColor,
																	fontSize: '18px',
																	marginRight: '28px'
																	}}
																>
																	{idx + 1}
																</div>
																{/* User avatar */}
																<div
																	style={{
																	width: '64px',
																	height: '64px',
																	background: '#d2dae4',
																	borderRadius: '12px',
																	marginRight: '32px',
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center'
																	}}
																>
																	<img
																	src={
																		user.userDisplayImage && user.userDisplayImage !== "none"
																		? user.userDisplayImage
																		: "/assets/img/usershimmer.png"
																	}
																	alt={user.username || "User"}
																	width={48}
																	height={48}
																	style={{ borderRadius: 8, objectFit: 'cover', maxWidth: '100%', maxHeight: '100%' }}
																	onError={e => {
																		e.target.onerror = null;
																		e.target.src = "/assets/img/usershimmer.png";
																	}}
																	/>
																</div>
																{/* User info */}
																<div style={{ flex: 1, paddingTop: 5 }}>
																	<div
																	className="text-inverse text-opacity-80"
																	style={{ color: inverseColor, fontWeight: 600, fontSize: '18px', marginBottom: '2px' }}
																	>
																		<span
																			style={{ cursor: 'pointer' }}
																			onClick={() => navigate(`/profile/${user.id || user.userId || user._id}`)}
																		>
																			{user.username}
																		</span>
																	</div>
																	<div style={{ color: inverseColor, fontSize: '15px', marginTop: '6px' }}>
																	{/* <div>
																		<span style={{ color: '#b6bdc7', minWidth: 56, display: 'inline-block' }}>Email:</span>
																		<span>{user.email}</span>
																	</div> */}
																	<div>
																		<span style={{ color: inverseColor, minWidth: 56, display: 'inline-block' }}>Last Activity</span>
																		<span>
																		{user.last_activity
																			? (
																			<>
																				<div>
																				<span style={{ color: inverseColor, minWidth: 56, display: 'inline-block' }}>Date:</span>
																				{(() => {
																					const d = new Date(user.last_activity);
																					const mm = String(d.getMonth() + 1).padStart(2, '0');
																					const dd = String(d.getDate()).padStart(2, '0');
																					const yyyy = d.getFullYear();
																					return `${mm}/${dd}/${yyyy}`;
																				})()}
																				</div>
																				<div>
																				<span style={{ color: inverseColor, minWidth: 56, display: 'inline-block' }}>Time:</span>
																				{new Date(user.last_activity).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
																				</div>
																			</>
																			)
																			: "--"}
																		</span>

																	</div>
																</div>
															</div>
														</div>
															))
															) : hardwareUsersLoading ? (
															<div style={{ textAlign: "center", color: inverseColor, padding: 32 }}>
																<span className="spinner-border spinner-border-sm" /> Loading users...
															</div>
															) : hardwareUsersError ? (
															<div style={{ color: "red", textAlign: "center", padding: 32 }}>
																{hardwareUsersError}
															</div>
															) : (
															<div style={{ color: inverseColor, textAlign: "center", padding: 32 }}>
																No users found for this device.
															</div>
															)}
														</div>
													</CardBody>
												</Card>
											</div>
													
											<div className={`tab-pane fade${activeTab === 'station-details' ? ' show active' : ''}`} id="station-details">
												{/* Station Status Card */}
												<Card style={{ background: "transparent", boxShadow: "none", marginBottom: "2rem" }}>
													<CardBody>
													<div
													className="d-flex fw-bold small mb-3 align-items-center"
													style={{
														position: 'absolute',
														top: '16px',
														right: '16px',
														cursor: 'pointer'
													}}
													>
														<ModalComponent
															lastConnectedData={lastConnectedData}
															lastCleanedData={lastCleanedData}
															ingredientsHistoryData={Array.isArray(ingHistory) ? ingHistory : []}
															deviceName={
															deviceType === 'barsys360'
																? "Barsys 360"
																: deviceType === 'coaster'
																? "Barsys Coaster"
																: deviceType === 'shakerpro'
																? "Barsys Shaker Pro"
																: "Barsys Device"
															}
															initialActiveTab="lastConnected"
															showLastConnectedTab={true}
															// initialActiveTab="lastConnected"
														/>
													</div>
													<div style={{ fontWeight: 600, fontSize: 14, marginBottom: 20, color: inverseColor }}>
														Last Connected:
														{stationDetails && stationDetails.last_connected
															? (
																<>
																	<span style={{ color: inverseColor, fontWeight: 400, marginLeft: 16 }}>
																	{new Date(stationDetails.last_connected).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
																	</span>
																	<span style={{ color: inverseColor, fontWeight: 400, marginLeft: 16 }}>
																	{new Date(stationDetails.last_connected).toLocaleDateString()}
																	</span>
																</>
																)
															: <span style={{ color: "#b6bdc7", fontWeight: 400, marginLeft: 16 }}>--</span>
															}
														</div>
														<table style={{ width: "100%", color: "#c9d1d9", fontSize: 15 }}>
															<thead style={{ color: "#b6bdc7", fontWeight: 600, fontSize: 13, letterSpacing: 1 }}>
																<tr style={{ borderBottom: "2px solid rgba(255,255,255,0.16)" }} >
																	<th align="left" style={{ padding: "8px 0", color: inverseColor }}>Station</th>
																	<th align="left" style={{ padding: "8px 0", color: inverseColor }}>Ingredient</th>
																	<th align="left" style={{ padding: "8px 0", color: inverseColor }}>Quantity</th>
																	<th align="left" style={{ padding: "8px 0", color: inverseColor }}>Last Cleaned</th>
																</tr>
															</thead>
															<tbody>
																{stationDetailsLoading ? (
																	<tr>
																	<td colSpan={4} style={{ textAlign: "center", padding: 32 }}>
																		<span className="spinner-border spinner-border-sm" /> Loading station details...
																	</td>
																	</tr>
																) : stationDetailsError ? (
																	<tr>
																	<td colSpan={4} style={{ color: 'red', textAlign: 'center', padding: 32 }}>
																		{stationDetailsError}
																	</td>
																	</tr>
																) : stationDetails && stationDetails.stations && stationDetails.stations.length > 0 ? (
																	stationDetails.stations.map((row, idx) => (
																	<tr key={row.station}>
																		<td style={{ padding: "12px 0", color: inverseColor }}>Station {row.station}</td>
																		<td style={{ color: inverseColor, padding: "12px 0", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default"}} title={row.ingredient_name}>
																		{truncate(row.ingredient_name, 25) || "n/a"}
																		</td>
																		<td style={{ padding: "12px 0", color: inverseColor }}>
																			{row.quantity !== null && row.quantity !== undefined
																			? Number(row.quantity).toFixed(2)
																			: "n/a"}
																		</td>
																		<td style={{ padding: "8px 0", color: inverseColor }}>
			{ row.last_cleaned
    ? new Intl.DateTimeFormat('en-US', { dateStyle:'short', timeStyle:'short' })
        .format(new Date(row.last_cleaned))
    : "--" }

																		</td>
																	</tr>
																	))
																) : (
																	<tr>
																	<td colSpan={4} style={{ textAlign: "center", padding: 32 }}>
																		No station details found for this device.
																	</td>
																	</tr>
																)}
															</tbody>
														</table>
													</CardBody>
												</Card>

												{/* Ingredient History Card */}
												<Card style={{ background: "transparent", boxShadow: "none" }}>
													<CardBody>
														<div
															className="d-flex fw-bold small mb-3 align-items-center"
															style={{
																position: 'absolute',
																top: '16px',
																right: '16px',
																cursor: 'pointer'
															}}
														>
															<ModalComponent
																lastConnectedData={lastConnectedData}
																lastCleanedData={lastCleanedData}
																ingredientsHistoryData={Array.isArray(ingHistory) ? ingHistory : []}
																deviceName={
																deviceType === 'barsys360'
																	? "Barsys 360"
																	: deviceType === 'coaster'
																	? "Barsys Coaster"
																	: deviceType === 'shakerpro'
																	? "Barsys Shaker Pro"
																	: "Barsys Device"
																}
																showLastConnectedTab={false}
																initialActiveTab="ingredientsHistory"
															/>
														</div>
														<div style={{ fontWeight: 600, color: inverseColor, fontSize: 14, marginBottom: 20 }}>
															Ingredient History
														</div>
														<table style={{ width: "100%", color: inverseColor, fontSize: 15 }}>
															<thead>
																<tr style={{
																color: "#b6bdc7",
																fontWeight: 600,
																fontSize: 13,
																borderBottom: "2px solid rgba(255,255,255,0.16)"
																}}>
																<th style={{ width: 80, padding: "16px 0", paddingRight: 24, color: inverseColor }}>#</th>
																<th align="left" style={{ padding: "16px 0", color: inverseColor }}>Ingredient</th>
																<th align="left" style={{ padding: "16px 0", color: inverseColor }}>Count</th>
																</tr>
															</thead>
														<tbody>
															{ingHistoryLoading ? (
																<tr>
																<td colSpan={3} style={{ textAlign: "center", padding: 32 }}>
																	<span className="spinner-border spinner-border-sm" /> Loading ingredients history...
																</td>
																</tr>
															) : ingHistoryError ? (
																<tr>
																<td colSpan={3} style={{ color: inverseColor, textAlign: 'center', padding: 32 }}>
																	{ingHistoryError}
																</td>
																</tr>
															) : Array.isArray(ingHistory) && ingHistory.length > 0 ? (
																ingHistory.slice(0, 10).map((row, idx, arr) => (
																<tr
																	key={row.ingredient || idx}
																	style={{
																	borderBottom:
																		idx !== arr.length - 1
																		? "1px solid rgba(255,255,255,0.16)"
																		: "1.5px solid rgba(255,255,255,0.16)"
																	}}
																>
																	<td style={{ color: inverseColor, fontWeight: 700, padding: "16px 0", paddingRight: 24 }}>{idx + 1}</td>
																	<td style={{ padding: "16px 0", color: inverseColor }}>
																	{row.ingredient}
																	</td>
																	<td style={{ padding: "16px 0" }}>
																	<span style={{ color: inverseColor }}>{row.count}</span>
																	</td>
																</tr>
																))
															) : (
																<tr>
																<td colSpan={3} style={{ textAlign: "center", padding: 32 }}>
																	No ingredient history found for this device.
																</td>
																</tr>
															)}
														</tbody>
													</table>
												</CardBody>
											</Card>
										</div>
										</div>
									</div>
									<div className="col-xl-4">
										<div className="desktop-sticky-top d-none d-lg-block">
											<Card className="mb-3">
												<div className="list-group list-group-flush">
													<div className="list-group-item fw-bold px-3 d-flex">
														<span className="flex-fill">Updates/Activities</span> 
														{/* <a href="#/" className="text-inverse text-opacity-50"><i className="fa fa-cog"></i></a> */}
													</div>
													<div className="list-group-item px-3">
														<div className="text-inverse text-opacity-80" style={{ paddingBottom: "10px" }}>
															<span><strong>Recently Made Drinks</strong></span>
														</div>
														<CardBody className="p-0">
															<div className="row gx-3 gy-2">
															{loadingRecentDrinks ? (
																<div className="col-12 py-3 text-center text-muted">Loading...</div>
															) : recentDrinks && recentDrinks.length > 0 ? (
																recentDrinks.slice(0, 2).map((drink, idx) => (
																<div key={drink.user_event_id || idx} className="col-12 col-md-6">
																	{/* Responsive Image Box */}
																	<div
																	style={{
																		width: "100%",
																		aspectRatio: "1 / 1",
																		borderRadius: "10px",
																		background: "#f8f8f8",
																		overflow: "hidden",
																		marginBottom: 6,
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																	}}
																	>
																	<img
																		src={drink.img || '/assets/img/glassshimmer.png'}
																		alt={drink.drink_name}
																		style={{
																		width: "100%",
																		height: "100%",
																		objectFit: "cover",
																		display: "block",
																		borderRadius: "8px"
																		}}
																		onError={e => {
																		if (!e.target.src.endsWith('glassshimmer.png')) {
																			e.target.src = '/assets/img/glassshimmer.png';
																		}
																		}}
																	/>
																	</div>
																	<div>
																	<small
																		className="text-inverse text-opacity-80"
																		style={{
																		paddingLeft: 2,
																		fontWeight: 500,
																		display: "block",
																		fontSize: 13,
																		textOverflow: "ellipsis",
																		overflow: "hidden",
																		whiteSpace: "nowrap"
																		}}
																		title={drink.drink_name}
																	>
																		{drink.drink_name}
																	</small>
																	</div>
																</div>
																))
															) : (
																<div className="col-12 py-3 text-muted">No drinks found.</div>
															)}
															</div>
														</CardBody>
														</div>

													<CardBody className="p-3">
														{/* Header Section */}
														<div className="mb-3">
															<div className="text-inverse text-opacity-80">
															<span>
																<strong>Most Repeated Drinks</strong>
															</span>
															</div>
														</div>

														{/* Images Row */}
														<div className="row gx-2 gy-2">
															{loadingMostRepeatedDrinks ? (
															<div className="col-12 py-3 text-center text-muted">Loading...</div>
															) : errorMostRepeatedDrinks ? (
															<div className="col-12 py-3 text-danger">{errorMostRepeatedDrinks}</div>
															) : mostRepeatedDrinks && mostRepeatedDrinks.length > 0 ? (
															mostRepeatedDrinks.slice(0, 4).map((drink, idx) => (
																<div key={drink.drink_name + idx} className="col-6 col-md-3 text-left">
																<div
																	style={{
																	width: "100%",
																	aspectRatio: "1 / 1",
																	borderRadius: "8px",
																	background: "#f5f6fa",
																	overflow: "hidden",
																	marginBottom: 5,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	}}
																>
																	<img
																	src={drink.img && drink.img.trim() && drink.img !== 'none'
																		? drink.img
																		: '/assets/img/glassshimmer.png'}
																	alt={drink.drink_name}
																	style={{
																		width: "100%",
																		height: "100%",
																		objectFit: "cover",
																		display: "block",
																		borderRadius: "8px"
																	}}
																	onError={e => {
																		if (!e.target.src.endsWith('glassshimmer.png')) {
																		e.target.src = '/assets/img/glassshimmer.png';
																		}
																	}}
																	/>
																</div>
																<div>
																	<small
																	className="text-inverse text-opacity-80"
																	style={{
																		paddingLeft: 2,
																		fontWeight: 500,
																		fontSize: 12,
																		display: "block",
																		textOverflow: "ellipsis",
																		overflow: "hidden",
																		whiteSpace: "nowrap"
																	}}
																	title={drink.drink_name}
																	>
																	{drink.drink_name}
																	</small>
																</div>
																</div>
															))
															) : (
															<div className="col-12 py-3 text-muted">No drinks found.</div>
															)}
														</div>
													</CardBody>
												</div>
												
											</Card>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</CardBody>
		</Card>
	)
}

export default HardwareProfile;